import Image from 'next/image';
import type {FC} from 'react';
import type {GetCompanyInfoResponse} from '../../../../core/repositories/companiesRepository';
import {cn} from '../../../../lib/classname';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {FollowCompanyTypeC} from '../../../../shared/components/FollowCompany/TypeC';

type Props = {
  data: GetCompanyInfoResponse;
  className?: string;
};
export const CompanyInfo: FC<Props> = ({data, className}) => {
  return (
    <>
      <CompanyInfoSP data={data} className={cn('pc:hidden', className)} />
      <CompanyInfoPC data={data} className={cn('hidden pc:flex', className)} />
    </>
  );
};

const CompanyInfoSP: FC<Props> = ({data, className}) => {
  return (
    <div className={cn('w-full flex flex-col gap-[16px]', className)}>
      <div className="bg-white shadow-md rounded-[12px] flex flex-col gap-8 p-16 mx-16">
        <div className="flex justify-between items-center">
          <CommonLink
            href={`/company/${data.id}`}
            className="row-span-2 hover:opacity-80 inline-block max-w-[68px]"
          >
            <Image
              src={data.logoURL}
              alt={data.name}
              width={100}
              height={75}
              className="object-contain"
            />
          </CommonLink>
          <FollowCompanyTypeC
            companyId={data.id}
            initialFollowerCount={data.followerCount}
            isInitiallyFollowed={data.followed}
          />
        </div>
        <CommonLink
          href={`/company/${data.id}`}
          className="font-bold text-[18px] leading-[27px] hover:opacity-80"
        >
          {data.name}
        </CommonLink>
        <div className="text-[14px] leading-[21px]">
          {data.industryCategoryName}
        </div>
      </div>

      {data.mainPhotoURL && (
        <CoverPhoto
          src={data.mainPhotoURL}
          name={data.name}
          className="mx-16"
        />
      )}
    </div>
  );
};

const CompanyInfoPC: FC<Props> = ({data, className}) => {
  return (
    <div className={cn('w-full flex flex-col gap-[16px]', className)}>
      <div className="bg-white shadow-md rounded-[12px] flex justify-between items-center p-16">
        <div className="grid grid-cols-[auto_1fr] grid-rows-2 gap-8">
          <CommonLink
            href={`/company/${data.id}`}
            className="row-span-2 hover:opacity-80 inline-block max-w-[68px]"
          >
            <Image
              src={data.logoURL}
              alt={data.name}
              width={100}
              height={75}
              className="object-contain"
            />
          </CommonLink>
          <CommonLink
            href={`/company/${data.id}`}
            className="font-bold text-[24px] leading-[26.4px] hover:opacity-80"
          >
            {data.name}
          </CommonLink>
          <div className="text-[14px] leading-[21px]">
            {data.industryCategoryName}
          </div>
        </div>
        <FollowCompanyTypeC
          companyId={data.id}
          initialFollowerCount={data.followerCount}
          isInitiallyFollowed={data.followed}
        />
      </div>

      {data.mainPhotoURL && (
        <CoverPhoto src={data.mainPhotoURL} name={data.name} />
      )}
    </div>
  );
};

const CoverPhoto: FC<{src: string; name: string; className?: string}> = ({
  src,
  name,
  className,
}) => {
  return (
    <div className={cn('rounded-16 overflow-hidden', className)}>
      <Image
        src={src}
        alt={name}
        width={948}
        height={392}
        className="object-contain w-full"
      />
    </div>
  );
};
